import { useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import "./OrderConfirmation.css";
import {
  fetchOrderById,
  fetchPreorderOrderDetails,
} from "../../api/orders.api";

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const CLEARANCE_GUIDELINES = [
  "Kindly note : Pls make sure to mention your phone number & address without fail .",
  "Shipping for the clearance sale will start from July 20th Only . Please make a purchase only if you have patience to wait . Kindly avoid DM asking for updates. We will contact you when it is ready.",
  "DIT / LIT / Factory QC issues are NOT COVERED at any circumstances",
  "Shipping costs are charged extra . Please ensure to make purchase in a single bill to avoid multiple charges. Each bill will be charged separately for delivery & cannot be clubbed.",
  "NO CANCELLATION, REFUND & RETURNS OR EXCHANGE",
];

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return "https://picsum.photos/200";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${BASE_URL}${imagePath.startsWith("/") ? imagePath : `/${imagePath}`}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isPreorderView = queryParams.get("type") === "preorder";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadOrder = isPreorderView
      ? fetchPreorderOrderDetails
      : fetchOrderById;

    loadOrder(orderId)
      .then((data) => {
        if (!mounted) return;
        setOrder(data);
      })
      .catch(() => {
        if (!mounted) return;
        setOrder(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isPreorderView, orderId]);


  const { subtotal, taxTotal, grandTotal } = useMemo(() => {
    if (!order?.items) {
      return { subtotal: 0, taxTotal: 0, grandTotal: 0 };
    }

    const subtotalCalc = order.items.reduce(
      (sum, i) =>
        sum + Number(i.unit_price || 0) * Number(i.quantity || 0),
      0
    );

    const taxCalc = order.items.reduce(
      (sum, i) =>
        sum + Number(i.unit_tax || 0) * Number(i.quantity || 0),
      0
    );

    return {
      subtotal: subtotalCalc,
      taxTotal: taxCalc,
      grandTotal: subtotalCalc + taxCalc,
    };
  }, [order]);

  const isRedeemOrder = useMemo(() => {
    if (isPreorderView) return false;
    if (!Array.isArray(order?.items) || order.items.length === 0) return false;

    return order.items.every(
      (orderItem) =>
        orderItem?.type === "redeem" || orderItem?.item?.is_redeem === true
    );
  }, [isPreorderView, order]);

  const totalRedeemPoints = useMemo(() => {
    if (!isRedeemOrder || !Array.isArray(order?.items)) return 0;

    return order.items.reduce((sum, orderItem) => {
      const points = Number(orderItem?.item?.points_required || 0);
      const quantity = Number(orderItem?.quantity || 0);
      return sum + points * quantity;
    }, 0);
  }, [isRedeemOrder, order]);

  const preorderItems = useMemo(() => {
    if (!isPreorderView || !order) return [];

    if (Array.isArray(order.items) && order.items.length > 0) {
      return order.items;
    }

    if (order.preorder_product) {
      return [
        {
          id: order.id,
          preorder_product: order.preorder_product,
          preorder_product_name: order.preorder_product?.name,
          quantity: order.quantity,
          deposit_amount_per_unit: order.deposit_amount_per_unit,
          product_tax_per_unit: order.product_tax_per_unit,
          total_deposit_amount: order.total_deposit_amount,
          total_amount: order.total_amount,
        },
      ];
    }

    return [];
  }, [isPreorderView, order]);

  const preorderSubtotal = useMemo(
    () =>
      preorderItems.reduce(
        (sum, item) =>
          sum + Number(item?.deposit_amount_per_unit || 0) * Number(item?.quantity || 0),
        0
      ),
    [preorderItems]
  );

  const preorderTaxTotal = useMemo(
    () =>
      preorderItems.reduce(
        (sum, item) =>
          sum + Number(item?.product_tax_per_unit || 0) * Number(item?.quantity || 0),
        0
      ),
    [preorderItems]
  );

  const preorderGrandTotal = useMemo(
    () => Number(order?.total_amount || preorderSubtotal + preorderTaxTotal || 0),
    [order, preorderSubtotal, preorderTaxTotal]
  );

  const renderClearanceGuidelines = () => (
    <>
      <h3 className="confirmation-delivery-title">
        STORE POLICY & SHIPPING GUIDELINES FOR CLEARANCE SALE
      </h3>

      <section className="confirmation-guidelines">
        {CLEARANCE_GUIDELINES.map((guideline) => (
          <p key={guideline} className="confirmation-guideline-item">
            {guideline}
          </p>
        ))}
      </section>
    </>
  );



  if (loading) {
    return (
      <div className="loading-wrap">
        <div className="loader" />
        <div>Loading order…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <p className="confirmation-center">
        Order not found
      </p>
    );
  }

  if (isPreorderView && preorderItems.length > 0) {
    return (
      <div className="confirmation-container">
        <h2 className="confirmation-thankyou">
          THANK YOU {order.user_name?.toUpperCase()}
        </h2>

        <p className="confirmation-subtext">
          Your preorder number is <strong>{orderId}</strong>.
          <br />
          We have received your preorder request successfully.
        </p>

        <div className="confirmation-header">
          <h3 className="confirmation-title">
            PRE-ORDER CONFIRMATION
          </h3>

          <button
            className="confirmation-print-btn"
            onClick={() => window.print()}
          >
            PRINT PRE-ORDER CONFIRMATION
          </button>
        </div>

        <div className="confirmation-print-area">
          <section className="confirmation-summary preorder-summary">
            <div className="confirmation-summary-header preorder-summary-header">
              <span>PRE-ORDER ITEM</span>
              <span>QTY</span>
              <span>DEPOSIT</span>
              <span>TAX</span>
            </div>

            {preorderItems.map((item) => {
              const preorderProduct = item?.preorder_product;
              return (
                <div className="confirmation-row preorder-row" key={item.id}>
                  <div className="confirmation-item">
                    <img
                      src={resolveImageUrl(preorderProduct?.image)}
                      alt={preorderProduct?.name || "Pre-order item"}
                    />
                    <div>
                      <p className="confirmation-product">
                        {preorderProduct?.name || item?.preorder_product_name || "Pre-order item"}
                      </p>
                      <p className="confirmation-brand">
                        Brand : {preorderProduct?.category_name || preorderProduct?.linked_product_name || "—"}
                      </p>
                    </div>
                  </div>

                  <div data-label="Qty">
                    {item?.quantity || 0}
                  </div>

                  <div data-label="Deposit">
                    ₹{item?.deposit_amount_per_unit || 0}
                  </div>

                  <div data-label="Tax">
                    ₹{item?.product_tax_per_unit || 0}
                  </div>
                </div>
              );
            })}

            <div className="confirmation-totals">
              <div>
                <span>Total Deposit</span>
                <span>₹{order.total_deposit_amount || preorderSubtotal}</span>
              </div>
              <div>
                <span>Total Tax</span>
                <span>₹{preorderTaxTotal}</span>
              </div>
              <div className="confirmation-grand">
                <span>Total Amount</span>
                <span>₹{preorderGrandTotal}</span>
              </div>
            </div>
          </section>

          {renderClearanceGuidelines()}

          <h3 className="confirmation-delivery-title">
            DELIVERY METHOD
          </h3>

          <div className="confirmation-delivery preorder-details">
            <div>
              <strong>EXPRESS DELIVERY</strong>
              <p className="confirmation-product">
                For Shipping Cost and Delivery details pls connect with Jomson.
              </p>
              <p className="confirmation-product confirmation-clearance-shipping">
                Shipping for the clearance sale will start from July 20th Only.
              </p>
            </div>

            <div>
              <strong>Contact Name :</strong> Jomson
              <br />
              <strong>Shipping contact :</strong> 7907628677 ( Only watsapp msg )
              <br />
              <a
                className="confirmation-product confirmation-contact-link"
                href="https://m.me/61590208060766"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Link : https://m.me/61590208060766
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isRedeemOrder) {
    return (
      <div className="confirmation-container">
        <h2 className="confirmation-thankyou">
          THANK YOU {order.user_name?.toUpperCase()}
        </h2>

        <p className="confirmation-subtext">
          Your reward order number is <strong>{orderId}</strong>.
          <br />
          Reward redemption is confirmed.
        </p>

        <div className="confirmation-header">
          <h3 className="confirmation-title">
            REWARD ORDER DETAILS
          </h3>

          <button
            className="confirmation-print-btn"
            onClick={() => window.print()}
          >
            PRINT REWARD DETAILS
          </button>
        </div>

        <div className="confirmation-print-area">
          <section className="confirmation-summary reward-summary">
            <div className="confirmation-summary-header reward-summary-header">
              <span>REWARD ITEM</span>
              <span>QTY</span>
              <span>REWARDS</span>
              <span>REDEEMED ON</span>
            </div>

            {order.items.map((orderItem) => {
              const data = orderItem.item;
              return (
                <div className="confirmation-row reward-row" key={orderItem.id}>
                  <div className="confirmation-item">
                    <img
                      src={resolveImageUrl(data?.image)}
                      alt={data?.name || "Reward item"}
                    />
                    <div>
                      <p className="confirmation-product">{data?.name || "Reward item"}</p>
                      <p className="confirmation-brand">Reward Redemption</p>
                    </div>
                  </div>

                  <div data-label="Qty">{orderItem.quantity}</div>

                  <div data-label="Rewards">
                    ₹{Number(data?.points_required || 0) * Number(orderItem.quantity || 0)}
                  </div>

                  <div data-label="Redeemed On">
                    {formatDateTime(orderItem.created_at)}
                  </div>
                </div>
              );
            })}

            <div className="confirmation-totals reward-totals">
              <div className="confirmation-grand reward-grand">
                <span>Total Redeemed Rewards</span>
                <span>₹{totalRedeemPoints}</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }



  return (
    <div className="confirmation-container">
      <h2 className="confirmation-thankyou">
        THANK YOU {order.user_name?.toUpperCase()}
      </h2>

      <p className="confirmation-subtext">
        Thank you for your order. Your order number is{" "}
        <strong>{orderId}</strong>.
        <br />
        A confirmation email has been sent to your email address.
      </p>

      <div className="confirmation-header">
        <h3 className="confirmation-title">
          ORDER CONFIRMATION
        </h3>

        <button
          className="confirmation-print-btn"
          onClick={() => window.print()}
        >
          PRINT ORDER CONFIRMATION
        </button>
      </div>

      <div className="confirmation-print-area">
        <section className="confirmation-summary">
          <div className="confirmation-summary-header">
            <span>ITEM</span>
            <span>QTY</span>
            <span>UNIT PRICE</span>
            <span>TAX</span>
          </div>

          {order.items.map((orderItem) => {
            const data = orderItem.item;
            const isCombo = orderItem.type === "combo";
            const isClearance = orderItem.type === "clearance";
            const brandLabel =
              data?.brand ||
              data?.category?.name ||
              (isClearance ? "Clearance Product" : "—");

            return (
              <div className="confirmation-row" key={orderItem.id}>
                <div className="confirmation-item">
                  <img
                    src={resolveImageUrl(data?.image)}
                    alt={data?.name || "Product"}
                  />
                  <div>
                    <p className="confirmation-product">
                      {data?.name}
                      {isCombo && (
                        <span className="confirmation-combo">
                          {" "}
                          (Combo)
                        </span>
                      )}
                      {isClearance && (
                        <span className="confirmation-combo">
                          {" "}
                          (Clearance)
                        </span>
                      )}
                    </p>
                    <p className="confirmation-brand">
                      Brand : {brandLabel}
                    </p>
                  </div>
                </div>

                <div data-label="Qty">
                  {orderItem.quantity}
                </div>

                <div data-label="Price">
                  ₹{orderItem.unit_price}
                </div>

                <div data-label="Tax">
                  ₹{orderItem.unit_tax}
                </div>
              </div>
            );
          })}

          <div className="confirmation-totals">
            <div>
              <span>Subtotal</span>
              <span>₹{subtotal}</span>
            </div>
            <div>
              <span>Total Tax</span>
              <span>₹{taxTotal}</span>
            </div>
            <div className="confirmation-grand">
              <span>Total</span>
              <span>₹{grandTotal}</span>
            </div>
          </div>
        </section>

        {renderClearanceGuidelines()}

        <h3 className="confirmation-delivery-title">
          DELIVERY METHOD
        </h3>

        <div className="confirmation-delivery">
          <div>
            <strong>EXPRESS DELIVERY</strong>
            <p className="confirmation-product">For Shipping Cost and Delivery details pls connect with Jomson.</p>
            <p className="confirmation-product confirmation-clearance-shipping">
              Shipping for the clearance sale will start from July 20th Only.
            </p>
          </div>

          <div>
            <strong>Contact Name :</strong>{" "}
            Jomson
            <br />
            <strong>Shipping contact :</strong>{" "}
            7907628677 ( Only watsapp msg )
            <br />
            <a
              className="confirmation-product confirmation-contact-link"
              href="https://m.me/61590208060766"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Link : https://m.me/61590208060766
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
